export const en = {
  app: {
    title: 'Agatha Admin',
    name: 'Agatha',
    admin: 'Admin',
  },
  nav: {
    clients: 'Clients',
    chats: 'Chats',
    crm: 'CRM',
    contacts: 'Contacts',
    sources: 'Sources',
    improving: 'Improving',
    playground: 'Playground',
    knowledge: 'Knowledge',
    remarks: 'Remarks',
    access: 'Access',
    users: 'Users',
    profile: 'Profile',
  },
  language: {
    label: 'Language',
    description: 'Interface language for this account. It also applies on the next sign-in.',
    en: 'English',
    ru: 'Русский',
    saved: 'Language updated',
    failed: 'Could not update language',
  },
  common: {
    email: 'Email',
    password: 'Password',
    name: 'Name',
    role: 'Role',
    loading: 'Loading…',
    saving: 'Saving…',
    updating: 'Updating…',
    creating: 'Creating…',
    copy: 'Copy',
    confirm: 'Confirm',
    reject: 'Reject',
    discard: 'Discard',
    send: 'Send',
    none: 'None',
    empty: 'Empty',
    you: 'You',
    ai: 'AI',
    op: 'Op',
    agata: 'Agatha',
    operator: 'Operator',
    system: 'System',
    client: 'Client',
    lead: 'Lead',
    panel: 'Panel',
    phone: 'Phone',
    channel: 'Channel',
    status: 'Status',
    funnel: 'Funnel',
    stage: 'Stage',
    domain: 'Domain',
    topic: 'Topic',
    dialog: 'Dialog',
    message: 'Message',
    run: 'Run',
    commit: 'Commit',
    resolution: 'Resolution',
    created: 'Created',
    updated: 'Updated',
    expires: 'Expires',
    history: 'History',
    details: 'Details',
    properties: 'Properties',
    duration: 'Duration',
    executionTime: 'Execution time',
    noRunSteps: 'No run steps yet.',
    openListing: 'Open on Resale Center',
    listingFallback: 'Listing on Resale Center',
    discardTitle: 'Discard changes?',
    discardBody: 'Unsaved edits will be lost if you close this dialog.',
    keepEditing: 'Keep editing',
    add: 'Add',
    addItem: 'Add item…',
    addArgument: 'Add argument',
    addMapping: 'Add mapping',
    from: 'From',
    to: 'To',
    defaultOptional: 'Default (optional)',
    justNow: 'just now',
    minutesAgo: (count: number) => `${count}m ago`,
    hoursAgo: (count: number) => `${count}h ago`,
    daysAgo: (count: number) => `${count}d ago`,
    updateFailed: 'Update failed',
    invalidEmail: 'Enter a valid email',
  },
  roles: {
    admin: 'Admin',
    teacher: 'Teacher',
    sales: 'Sales',
    descriptions: {
      admin: 'Full access, including user management',
      teacher: 'Every section except user management',
      sales: 'Client chats and CRM',
    },
    buyer: 'Buyer',
    agent: 'Agent',
    owner: 'Owner',
    other: 'Other',
  },
  channels: {
    widget: 'Widget',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
  },
  handleKinds: {
    phone: 'Phone',
    email: 'Email',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
  },
  dialogStatus: {
    label: 'Agatha',
    ai_active: 'Leading the dialog',
    operator_owned: 'On pause',
    closed: 'Finished',
    escalated: 'Handed off',
  },
  login: {
    title: 'Sign in',
    passwordRequired: 'Password is required',
    failed: 'Login failed',
    submitting: 'Signing in…',
    submit: 'Sign in',
  },
  invite: {
    title: 'Accept invite',
    asRole: (role: string) => `Create your account as ${role}`,
    fallback: 'Create your Agatha account',
    passwordMin: 'Password must be at least 8 characters',
    confirmPassword: 'Confirm your password',
    mismatch: 'Passwords do not match',
    failed: 'Registration failed',
    notFound: 'Invite not found',
    submitting: 'Creating account…',
    submit: 'Create account',
  },
  profile: {
    title: 'Profile',
    description: 'Account settings',
    account: 'Account',
    accountHint: 'Update your display name and password.',
    nameRequired: 'Name is required',
    currentPassword: 'Current password',
    currentPasswordRequired: 'Current password is required',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    confirmNewRequired: 'Confirm your new password',
    mismatch: 'Passwords do not match',
    passwordMin: 'Password must be at least 8 characters',
    saveName: 'Save name',
    changePassword: 'Change password',
    nameUpdated: 'Name updated',
    passwordUpdated: 'Password updated',
    logout: 'Log out',
  },
  users: {
    title: 'Users',
    description: 'Accounts, roles, and invites',
    list: 'Users',
    roles: 'Roles',
    invites: 'Invites',
    empty: 'No users',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'User updated',
    saveFailed: 'Could not update user',
    loadFailed: 'Failed to load users',
    access: 'Access',
    invitesHint:
      'Create a single-use link. The invitee chooses their email and password; no email confirmation.',
    createInvite: 'Create invite link',
    creatingInvite: 'Creating…',
    inviteCreated: 'Invite link created',
    inviteFailed: 'Failed to create invite',
    inviteCopied: 'Invite link copied',
    copyFailed: 'Could not copy link',
    pending: 'Pending invites',
    noPending: 'No pending invites.',
    invitesLoadFailed: 'Failed to load invites',
    amocrm: 'amoCRM',
    amoEmpty: 'No amoCRM users synced yet. Configure AMOCRM_* credentials to pull the roster.',
    amoLoadFailed: 'Failed to load amoCRM users',
    amoLinked: 'amoCRM user linked',
    amoLinkFailed: 'Could not link amoCRM user',
    agataUser: 'Agatha user',
    amoUnlinked: 'Not linked',
    amoStatus: 'Status',
    amoActive: 'Active',
    amoInactive: 'Inactive',
    amoAdmin: 'Admin',
  },
  permissions: {
    can_view_chats: 'Chats',
    can_view_crm: 'CRM',
    can_use_playground: 'Playground',
    can_edit_knowledge_base: 'Knowledge',
    can_view_remarks: 'Remarks',
    can_manage_users: 'Users',
    can_invite_users: 'Invites',
  },
  chats: {
    title: 'Chats',
    description: 'Client conversations',
    search: 'Search chats',
    empty: 'No client chats yet',
    noMatches: 'No matches',
    noMessages: 'No messages',
    select: 'Select a chat',
    notFound: 'Chat not found',
    loading: 'Loading dialog',
    back: 'Chats',
    thread: 'Chat',
    messagePlaceholder: 'Message…',
    send: 'Send',
    sent: 'Message sent',
    sendFailed: 'Could not send message',
    sendWhatsAppOnly: 'Sending is available for WhatsApp chats only.',
    agataPause60: 'Pause Agatha for 60 minutes',
    agataStop: 'Stop Agatha',
    agataResume: 'Turn Agatha back on',
    agataStopped: 'Stopped until turned on',
    agataLocked: 'Handed off to a human — Agatha cannot be turned back on',
    agataUpdated: 'Agatha status updated',
    agataFailed: 'Could not update Agatha',
    agataPauseMinutes: (mins: number) =>
      mins === 1 ? '1 min left' : `${mins} min left`,
    pauseBeforeSendTitle: 'Agatha is leading this chat',
    pauseBeforeSendHint:
      'Pause Agatha for 60 minutes so she does not reply over your message, or send without pausing.',
    pauseAndSend: 'Pause 60 min and send',
    sendWithoutPause: 'Send without pausing',
  },
  crm: {
    title: 'CRM',
    description: 'What Agatha would write to the funnel',
    noCards: 'No cards',
    moveStage: 'Stage',
    whatChanged: 'What changed about this lead?',
    whatChangedHint:
      'Agatha will remember this for the sales team. It is not used in messages to the lead.',
    notePlaceholder: 'What changed?',
    saveNote: 'Remember',
    cancel: 'Cancel',
    salesMemory: 'Stage notes',
    salesMemoryEmpty: 'No stage notes yet',
    moveFailed: 'Could not change the stage',
    saveFailed: 'Could not save changes',
    saved: 'Saved',
    back: 'CRM',
    cardTitle: 'Lead card',
    cardDescription: 'CRM card details',
    cardNotFound: 'Card not found',
    cardLoadFailed: 'Failed to load card',
    openChat: 'Open chat',
    openCard: 'Open in CRM',
    summary: 'Description',
    summaryPlaceholder: 'Who the client is, goal, budget, district, readiness…',
    nameLabel: 'Name',
    leadBucket: 'Lead heat',
    leadBucketNone: 'Not set',
    leadSource: 'Source',
    leadSourceNone: 'Not set',
    sourcesTitle: 'Lead sources',
    sourcesDescription: 'Source values, WhatsApp code phrases, and client links',
    sourcesEmpty: 'No source phrases yet',
    sourcesLoadFailed: 'Failed to load phrases',
    sourcesAdd: 'Add',
    sourcesEdit: 'Edit',
    sourcesSave: 'Save',
    sourcesDelete: 'Delete',
    sourcesDeleteConfirm: 'Delete this source phrase?',
    sourcesDeleteConfirmHint: 'Clients with this link will no longer match this source.',
    sourcesPhrase: 'Code phrase',
    sourcesPhrasePlaceholder: 'Exact WhatsApp text the client sends',
    sourcesLanguage: 'Language',
    sourcesEnabled: 'Enabled',
    sourcesCopyLink: 'Copy link',
    sourcesOpenLink: 'Open',
    sourcesLinkCopied: 'Link copied',
    sourcesWaMeMissing: 'Set AGATA_WHATSAPP_PHONE to build client links',
    sourcesCreateTitle: 'Add source phrase',
    sourcesCreateHint: 'Binds a CRM source value to a WhatsApp code phrase and wa.me link.',
    sourcesEditTitle: 'Edit source phrase',
    sourcesCreateFailed: 'Could not create the phrase',
    sourcesUpdateFailed: 'Could not update the phrase',
    sourcesDeleteFailed: 'Could not delete the phrase',
    fields: 'Fields',
    responsible: 'Owner',
    pickFunnel: 'Choose funnel',
    addCard: 'Add card',
    createTitle: 'New card',
    createHint: 'Creates a local CRM card in the current funnel.',
    namePlaceholder: 'Lead name',
    phonePlaceholder: 'Phone',
    budgetPlaceholder: 'Budget (฿)',
    create: 'Create',
    createFailed: 'Could not create the card',
    activity: 'Activity',
    activityEmpty: 'No activity yet',
    activityFromAmo: 'amoCRM',
    activityFromOperator: 'Operator',
    activityCleared: 'cleared',
    activityCommentPlaceholder: 'Add a comment to the activity…',
    activityCommentSend: 'Add comment',
    activityCommentFailed: 'Could not add the comment',
    contacts: 'Contacts',
    contactsEmpty: 'No contacts',
    contactsGalleryEmpty: 'No contacts yet',
    contactsLoadFailed: 'Failed to load contacts',
    contactsSearch: 'Search contacts',
    contactCards: (count: number) =>
      count === 1 ? '1 card' : `${count} cards`,
    contactMain: 'Main',
    contactAdd: 'Add contact',
    contactCreate: 'Create contact',
    contactCreateHint: 'Creates a local contact and links it to this card.',
    contactLink: 'Link existing',
    contactLinkTitle: 'Link contact',
    contactLinkHint: 'Attach an existing contact to this card.',
    contactLinkEmpty: 'No contacts to link',
    contactUnlink: 'Unlink',
    contactUnlinkFailed: 'Could not unlink contact',
    contactNamePlaceholder: 'Contact name',
    contactPhonePlaceholder: 'Phone',
    contactAddFailed: 'Could not add contact',
    contactLinkFailed: 'Could not link contact',
    responsibleNone: 'Unassigned',
    dialogNone: 'No dialog',
    contactTitle: 'Contact',
    contactDescription: 'CRM contact details',
    contactNotFound: 'Contact not found',
    contactLoadFailed: 'Failed to load contact',
    backToCard: 'Card',
    backToContacts: 'Contacts',
    handles: 'Messaging channels',
    handlesEmpty: 'No phone, email, or messenger handles',
    channelAdd: 'Add channel',
    channelCreate: 'Create channel',
    channelCreateHint: 'Adds a phone, email, or messenger channel to this contact.',
    channelKind: 'Channel type',
    channelValuePlaceholder: 'Phone, email, or chat id',
    channelAddFailed: 'Could not add channel',
    channelSaveFailed: 'Could not update channel',
    channelUnlink: 'Unlink channel',
    channelUnlinkFailed: 'Could not unlink channel',
    linkedCards: 'CRM cards',
    linkedCardsEmpty: 'No linked cards',
    names: {
      'Лиды в работе': 'Leads in progress',
      'Отложенный спрос': 'Deferred demand',
      'Партнерская программа/Агенты': 'Partner program / Agents',
      Продавцы: 'Sellers',
      'Пост продажный сервис': 'Post-sale service',
      Архив: 'Archive',
      Неразобранное: 'Unsorted',
      'новый лид': 'New lead',
      'первичное касание': 'First touch',
      'не выходит на связь': 'No contact',
      'связались/подтвердили интерес': 'Contacted / interest confirmed',
      'Выявлены потребности': 'Needs identified',
      'подбор объекта': 'Property selection',
      'Предложения отправлены': 'Offers sent',
      'Переговоры/обсуждение': 'Negotiation',
      'назначен просмотр/встреча': 'Viewing scheduled',
      'Подготовка сделки/депозит': 'Deal preparation / deposit',
      'На сделке': 'In the deal',
      'выплата комиссии': 'Commission payout',
      'некорректный контакт': 'Invalid contact',
      'вышел на контакт': 'Reached out',
      'квалификация проведена': 'Qualified',
      Пассивный: 'Passive',
      активный: 'Active',
      база: 'Database',
      интересуется: 'Interested',
      'Отправили информацию': 'Information sent',
      'Подключён/активирован': 'Connected / activated',
      'Привёл первого клиента': 'Brought the first client',
      'Активный партнёр': 'Active partner',
      Неактивный: 'Inactive',
      'Новый продавец': 'New seller',
      'ОЖИДАЕМ информацию': 'Waiting for information',
      'ПОдготовка объекта': 'Preparing the property',
      'ОТПРАВКА СММ/МЕНЕДЖЕРАМ': 'Sent to SMM / managers',
      'Размещен на сайте': 'Published on the site',
      'актуализация объекта': 'Property refresh',
      'СРОЧНАЯ ПРОДАЖА': 'Urgent sale',
      'Нет ответа': 'No reply',
      РАЗОБРАТЬ: 'Sort out',
    },
  },
  systemKind: {
    crm_shadow: 'CRM shadow',
    crm: 'CRM',
    reminder: 'Reminder',
    routing: 'Routing',
    info: 'System',
  },
  playground: {
    title: 'Playground',
    description: 'Chat as a marketplace visitor against the live reply pipeline',
    sessions: 'Sessions',
    recent: 'Recent playground sessions',
    noSessions: 'No sessions yet.',
    noMessages: '(no messages)',
    messageCount: (count: number) => `${count} msgs`,
    remarksCount: (open: number, total: number) => `${open}/${total} remarks`,
    startFailed: 'Failed to start session',
    loadFailed: 'Failed to load dialog',
    noSession: 'No session',
    skipped: 'AI skipped — a reply is still pending approval',
    sendFailed: 'Send failed',
    pushAdded: (number: number, offset: string) =>
      `No-reply push ${number}/3 added as an Agatha message (+${offset})`,
    movedDeferred: 'Lead moved to «Deferred demand»',
    noSeries: 'No active reminder series — Agatha has to ask a question first',
    simulate: (step: number) => `Simulate no-reply step ${step}`,
    simulateButton: 'Simulate no-reply',
    simulationFailed: 'Simulation failed',
    newChat: 'New chat',
    loadingDialog: 'Loading dialog',
    startHint: 'Send a message to start',
    thinking: 'Thinking',
    placeholder: 'Ask as a buyer…',
    send: 'Send',
    loadRunFailed: 'Failed to load run',
    hidePanel: 'Hide panel',
    showPanel: 'Show panel',
    diagnostics: 'Diagnostics',
  },
  knowledge: {
    title: 'Knowledge',
    description: 'Browse the knowledge graph and edit it through a confirm-gated chat.',
    snapshot: 'Snapshot',
    backup: 'Backup',
    restore: 'Restore',
    refresh: 'Refresh graph',
    graph: 'Graph',
    chat: 'Chat',
    details: 'Details',
    invalidFile: 'Invalid JSON file',
    notObject: 'Snapshot must be a JSON object',
    emptyEntities: 'Snapshot must include a non-empty entities array',
    updated: 'Knowledge graph updated',
    applied: (count: number) => `Applied ${count} change(s)`,
    chatFailed: 'Knowledge chat failed',
    downloaded: (count: number) => `Downloaded ${count} entities`,
    backupFailed: 'Backup failed',
    restored: (count: number) => `Restored ${count} entities`,
    cleared: (count: number) => ` (cleared ${count})`,
    restoreFailed: 'Restore failed',
    confirmPrompt: 'Confirm the proposed mutations.',
    rejectPrompt: 'Reject the proposed mutations.',
    rejectedReply: 'Understood — no changes were applied.',
    loadFailed: 'Failed to load the knowledge graph',
    proposed: 'Proposed changes',
    placeholder: 'Ask or request a knowledge change…',
    intro: 'Ask about entities, propose edits, then confirm the plan before anything is written.',
    selectNode: 'Select a node on the graph to inspect its properties and edges.',
    selectNodeShort: 'Select a node on the graph.',
    noProperties: 'No properties',
    outgoing: (count: number) => `Outgoing edges (${count})`,
    incoming: (count: number) => `Incoming edges (${count})`,
    entityDetails: 'Entity details',
    entityHint: 'Knowledge entity properties and edges',
  },
  remarks: {
    title: 'Remarks',
    forDialog: (id: string) => `Remarks for dialog ${id}`,
    summary: (open: number, total: number) =>
      `${open} open of ${total}. Drag cards between columns; the local fixing agent moves them to Fixed.`,
    openDialog: 'Open dialog',
    clearFilter: 'Clear filter',
    loading: 'Loading remarks…',
    loadFailed: 'Failed to load remarks',
    moveFailed: 'Failed to move remark',
    remarkId: 'Remark id',
    events: 'History',
    moveTo: 'Move to',
    commitRef: (ref: string) => `commit ${ref}`,
    sheetHint: 'Remark details and history',
    updatedAt: (when: string) => `Updated ${when}`,
    statuses: {
      new: 'New',
      in_progress: 'In progress',
      fixed: 'Fixed',
      verified: 'Verified',
      rejected: 'Rejected',
    },
    categories: {
      reply_quality: 'Reply quality',
      grounding: 'Grounding',
      journey: 'Journey',
      tone: 'Tone',
      tooling: 'Tooling',
      knowledge: 'Knowledge',
      other: 'Other',
    },
    severity: {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    eventCreated: 'Created',
  },
  review: {
    proposed: (count: number) => `Proposed remarks (${count})`,
    createOne: 'Create remark',
    createMany: (count: number) => `Create ${count} remarks`,
    created: (count: number) => `Created ${count} remark(s)`,
    failed: 'Review chat failed',
    empty: 'Start a chat to leave remarks about this dialog.',
    list: (count: number) => `Remarks (${count})`,
    board: 'Board',
    loading: 'Loading review thread…',
    intro:
      'Describe what went wrong in this dialog. The reviewer grounds your feedback in the transcript and run diagnostics, proposes remarks, and files them after you confirm.',
    reviewing: 'Reviewing…',
    placeholder: 'What should Agatha have done differently?',
    send: 'Send',
  },
  diagnostics: {
    thinking: 'Agent Thinking',
    memory: 'Memory',
    remarks: 'Remarks',
    skipped: (expr: string) => `skipped: ${expr}`,
    notReached: 'not reached',
    tokens: (input: string, output: string) => `tokens ${input} / ${output}`,
    query: (key: string) => `query ${key}`,
    contextAfter: 'Context after run',
    contextHint: 'Shared memory and step outputs',
    tool: (name: string) => `Tool: ${name}`,
    skippedCondition: 'Skipped condition',
    ifFalseStop: 'If false: stop pipeline',
    ifFalseContinue: 'If false: continue',
    resolved: 'Resolved values',
    contextIn: 'Context in',
    contextOut: 'Context / tool out',
    received: 'Received',
    guidance: 'Guidance written for reply composer',
    returned: 'Returned',
    generation: 'Generation instructions',
    requestMessages: 'Request messages',
    response: 'Response',
    finalContext: 'Final context',
    emptySkill: 'Skill produced no usable result (empty query / missing params / guidance-only).',
    emptyStep: 'Step produced no usable result.',
    version: (id: string) => `version ${id}`,
    startMemory: 'Start a chat to inspect durable memory for this dialog.',
    loadingMemory: 'Loading memory…',
    memoryFailed: 'Failed to load memory',
    noMemory:
      'No memory written yet. After the agent advances the journey or remembers facts, they appear here.',
    interlocutorSummary: 'Interlocutor summary',
    dialogSummary: 'Dialog notes',
    slots: (count: number) => `Slots (${count})`,
    untitled: 'untitled',
    collapseGraph: 'Collapse graph',
    expandGraph: 'Expand graph',
    promptChars: (count: string) => `${count} chars`,
    prompt: 'prompt',
    skills: 'skills:',
    loadingRun: 'Loading run…',
    noSteps: 'No step diagnostics for this run.',
    stepsHint:
      'Tool calls appear here as a diagram after each reply. Click a block to expand details.',
    notReachedReason: (reason: string) => `Not reached: ${reason}`,
    llm: {
      skill_route: 'Choose skills',
      compose_reply: 'Compose reply',
      extract_slots: 'Extract memory',
    },
    status: {
      succeeded: 'Succeeded',
      failed: 'Failed',
      skipped: 'Skipped',
      running: 'Running',
      empty: 'Empty',
      not_reached: 'Not reached',
    },
    kind: {
      step: 'Step',
      skill: 'Skill',
      skill_step: 'Skill step',
      llm: 'LLM',
      context: 'Context',
    },
  },
  fields: {
    budget: 'Budget',
    district: 'District',
    property_type: 'Property type',
    handoff_reason: 'Handoff reason',
    owner_location: 'Owner location',
    language: 'Language',
    bedrooms: 'Bedrooms',
    role: 'Role',
    partner: 'Partner',
    partner_commission: 'Partner commission',
    advertising: 'Advertising',
    call_recording: 'Call recording',
    lead_bucket: 'Lead heat',
    lead_source: 'Source',
    hot: 'Hot',
    warm: 'Warm',
    junk: 'Junk',
    quiz: 'Quiz',
    alliance: 'Alliance',
    marketplace: 'Marketplace',
    dialog_summary: 'Description',
    ad_id: 'Ad ID',
    ad_set_id: 'Ad set ID',
    campaign_name: 'Campaign name',
    utm_source: 'utm_source',
    utm_medium: 'utm_medium',
    utm_campaign: 'utm_campaign',
    utm_content: 'utm_content',
    utm_term: 'utm_term',
    utm_referrer: 'utm_referrer',
    email: 'Email',
    channel: 'Channel',
    phone: 'Phone',
    whatsapplid_wz: 'WhatsApp LID',
    whatsapplid_waapi: 'WhatsApp LID (WaAPI)',
    tgid: 'Telegram ID',
    tgusername: 'Telegram username',
    telegram: 'Telegram',
    должность: 'Position',
    город: 'City',
    сфера: 'Industry',
    опыт: 'Experience',
    комиссия_партнера: 'Partner commission',
  },
};

export type Messages = typeof en;

export const ru: Messages = {
  app: {
    title: 'Админка Agatha',
    name: 'Agatha',
    admin: 'Админка',
  },
  nav: {
    clients: 'Клиенты',
    chats: 'Чаты',
    crm: 'CRM',
    contacts: 'Контакты',
    sources: 'Источники',
    improving: 'Улучшение',
    playground: 'Песочница',
    knowledge: 'Знания',
    remarks: 'Замечания',
    access: 'Доступ',
    users: 'Пользователи',
    profile: 'Профиль',
  },
  language: {
    label: 'Язык',
    description: 'Язык интерфейса этой учётной записи. Он сохранится и для следующего входа.',
    en: 'English',
    ru: 'Русский',
    saved: 'Язык обновлён',
    failed: 'Не удалось сменить язык',
  },
  common: {
    email: 'Эл. почта',
    password: 'Пароль',
    name: 'Имя',
    role: 'Роль',
    loading: 'Загрузка…',
    saving: 'Сохранение…',
    updating: 'Обновление…',
    creating: 'Создание…',
    copy: 'Копировать',
    confirm: 'Подтвердить',
    reject: 'Отклонить',
    discard: 'Сбросить',
    send: 'Отправить',
    none: 'Нет',
    empty: 'Пусто',
    you: 'Вы',
    ai: 'ИИ',
    op: 'Оп',
    agata: 'Agatha',
    operator: 'Оператор',
    system: 'Система',
    client: 'Клиент',
    lead: 'Лид',
    panel: 'Панель',
    phone: 'Телефон',
    channel: 'Канал',
    status: 'Статус',
    funnel: 'Воронка',
    stage: 'Этап',
    domain: 'Домен',
    topic: 'Тема',
    dialog: 'Диалог',
    message: 'Сообщение',
    run: 'Прогон',
    commit: 'Коммит',
    resolution: 'Решение',
    created: 'Создано',
    updated: 'Обновлено',
    expires: 'Истекает',
    history: 'История',
    details: 'Подробности',
    properties: 'Свойства',
    duration: 'Длительность',
    executionTime: 'Время выполнения',
    noRunSteps: 'Шагов прогона пока нет.',
    openListing: 'Открыть на Resale Center',
    listingFallback: 'Объект на Resale Center',
    discardTitle: 'Сбросить изменения?',
    discardBody: 'Несохранённые правки пропадут, если закрыть это окно.',
    keepEditing: 'Продолжить редактирование',
    add: 'Добавить',
    addItem: 'Добавить пункт…',
    addArgument: 'Добавить аргумент',
    addMapping: 'Добавить соответствие',
    from: 'Откуда',
    to: 'Куда',
    defaultOptional: 'По умолчанию (необязательно)',
    justNow: 'только что',
    minutesAgo: (count: number) => `${count} мин. назад`,
    hoursAgo: (count: number) => `${count} ч. назад`,
    daysAgo: (count: number) => `${count} дн. назад`,
    updateFailed: 'Не удалось обновить',
    invalidEmail: 'Введите корректный адрес',
  },
  roles: {
    admin: 'Администратор',
    teacher: 'Преподаватель',
    sales: 'Продажи',
    descriptions: {
      admin: 'Полный доступ, включая управление пользователями',
      teacher: 'Все разделы, кроме управления пользователями',
      sales: 'Чаты с клиентами и CRM',
    },
    buyer: 'Покупатель',
    agent: 'Агент',
    owner: 'Собственник',
    other: 'Другое',
  },
  channels: {
    widget: 'Виджет',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
  },
  handleKinds: {
    phone: 'Телефон',
    email: 'Email',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
  },
  dialogStatus: {
    label: 'Агата',
    ai_active: 'Ведёт диалог',
    operator_owned: 'На паузе',
    closed: 'Закончила работу',
    escalated: 'Передана человеку',
  },
  login: {
    title: 'Вход',
    passwordRequired: 'Введите пароль',
    failed: 'Не удалось войти',
    submitting: 'Вход…',
    submit: 'Войти',
  },
  invite: {
    title: 'Принять приглашение',
    asRole: (role: string) => `Создайте учётную запись с ролью «${role}»`,
    fallback: 'Создайте учётную запись Agatha',
    passwordMin: 'Пароль должен быть не короче 8 символов',
    confirmPassword: 'Подтвердите пароль',
    mismatch: 'Пароли не совпадают',
    failed: 'Не удалось зарегистрироваться',
    notFound: 'Приглашение не найдено',
    submitting: 'Создание учётной записи…',
    submit: 'Создать учётную запись',
  },
  profile: {
    title: 'Профиль',
    description: 'Настройки учётной записи',
    account: 'Учётная запись',
    accountHint: 'Имя и пароль.',
    nameRequired: 'Введите имя',
    currentPassword: 'Текущий пароль',
    currentPasswordRequired: 'Введите текущий пароль',
    newPassword: 'Новый пароль',
    confirmNewPassword: 'Подтвердите новый пароль',
    confirmNewRequired: 'Подтвердите новый пароль',
    mismatch: 'Пароли не совпадают',
    passwordMin: 'Пароль должен быть не короче 8 символов',
    saveName: 'Сохранить имя',
    changePassword: 'Сменить пароль',
    nameUpdated: 'Имя обновлено',
    passwordUpdated: 'Пароль обновлён',
    logout: 'Выйти',
  },
  users: {
    title: 'Пользователи',
    description: 'Учётные записи, роли и приглашения',
    list: 'Пользователи',
    roles: 'Роли',
    invites: 'Приглашения',
    empty: 'Нет пользователей',
    edit: 'Изменить',
    save: 'Сохранить',
    cancel: 'Отмена',
    saved: 'Пользователь обновлён',
    saveFailed: 'Не удалось обновить пользователя',
    loadFailed: 'Не удалось загрузить пользователей',
    access: 'Доступ',
    invitesHint:
      'Одноразовая ссылка. Приглашённый сам указывает почту и пароль, подтверждение почты не нужно.',
    createInvite: 'Создать ссылку',
    creatingInvite: 'Создание…',
    inviteCreated: 'Ссылка создана',
    inviteFailed: 'Не удалось создать приглашение',
    inviteCopied: 'Ссылка скопирована',
    copyFailed: 'Не удалось скопировать ссылку',
    pending: 'Ожидающие приглашения',
    noPending: 'Нет ожидающих приглашений.',
    invitesLoadFailed: 'Не удалось загрузить приглашения',
    amocrm: 'amoCRM',
    amoEmpty:
      'Пользователи amoCRM ещё не синхронизированы. Настройте AMOCRM_*, чтобы подтянуть список.',
    amoLoadFailed: 'Не удалось загрузить пользователей amoCRM',
    amoLinked: 'Связь с amoCRM сохранена',
    amoLinkFailed: 'Не удалось связать пользователя amoCRM',
    agataUser: 'Пользователь Агаты',
    amoUnlinked: 'Не связан',
    amoStatus: 'Статус',
    amoActive: 'Активен',
    amoInactive: 'Неактивен',
    amoAdmin: 'Админ',
  },
  permissions: {
    can_view_chats: 'Чаты',
    can_view_crm: 'CRM',
    can_use_playground: 'Песочница',
    can_edit_knowledge_base: 'База знаний',
    can_view_remarks: 'Замечания',
    can_manage_users: 'Пользователи',
    can_invite_users: 'Приглашения',
  },
  chats: {
    title: 'Чаты',
    description: 'Переписки с клиентами',
    search: 'Поиск по чатам',
    empty: 'Клиентских чатов пока нет',
    noMatches: 'Ничего не найдено',
    noMessages: 'Нет сообщений',
    select: 'Выберите чат',
    notFound: 'Чат не найден',
    loading: 'Загрузка диалога',
    back: 'Чаты',
    thread: 'Чат',
    messagePlaceholder: 'Сообщение…',
    send: 'Отправить',
    sent: 'Сообщение отправлено',
    sendFailed: 'Не удалось отправить',
    sendWhatsAppOnly: 'Отправка доступна только для чатов WhatsApp.',
    agataPause60: 'Пауза Агаты на 60 минут',
    agataStop: 'Остановить Агату',
    agataResume: 'Включить Агату',
    agataStopped: 'Остановлена до включения',
    agataLocked: 'Диалог передан человеку — Агату нельзя включить снова',
    agataUpdated: 'Статус Агаты обновлён',
    agataFailed: 'Не удалось обновить Агату',
    agataPauseMinutes: (mins: number) =>
      mins === 1 ? 'осталась 1 мин' : `осталось ${mins} мин`,
    pauseBeforeSendTitle: 'Агата ведёт этот диалог',
    pauseBeforeSendHint:
      'Поставьте Агату на паузу на 60 минут, чтобы она не ответила поверх вашего сообщения, или отправьте без паузы.',
    pauseAndSend: 'Пауза 60 мин и отправить',
    sendWithoutPause: 'Отправить без паузы',
  },
  crm: {
    title: 'CRM',
    description: 'Что Agatha записала бы в воронку',
    noCards: 'Нет карточек',
    moveStage: 'Этап',
    whatChanged: 'Что изменилось по этому лиду?',
    whatChangedHint:
      'Agatha запомнит это для специалистов по продажам и не будет использовать в сообщениях лиду.',
    notePlaceholder: 'Что изменилось?',
    saveNote: 'Запомнить',
    cancel: 'Отмена',
    salesMemory: 'Заметки по этапу',
    salesMemoryEmpty: 'Заметок по этапу пока нет',
    moveFailed: 'Не удалось сменить этап',
    saveFailed: 'Не удалось сохранить',
    saved: 'Сохранено',
    back: 'CRM',
    cardTitle: 'Карточка',
    cardDescription: 'Подробности карточки CRM',
    cardNotFound: 'Карточка не найдена',
    cardLoadFailed: 'Не удалось загрузить карточку',
    openChat: 'Открыть чат',
    openCard: 'Открыть в CRM',
    summary: 'Описание',
    summaryPlaceholder: 'Кто клиент, цель, бюджет, район, готовность…',
    nameLabel: 'Имя',
    leadBucket: 'Температура лида',
    leadBucketNone: 'Не задано',
    leadSource: 'Источник',
    leadSourceNone: 'Не задано',
    sourcesTitle: 'Источники',
    sourcesDescription: 'Значения Источника, кодовые фразы WhatsApp и ссылки для клиентов',
    sourcesEmpty: 'Пока нет связок источника и фразы',
    sourcesLoadFailed: 'Не удалось загрузить фразы',
    sourcesAdd: 'Добавить',
    sourcesEdit: 'Изменить',
    sourcesSave: 'Сохранить',
    sourcesDelete: 'Удалить',
    sourcesDeleteConfirm: 'Удалить эту кодовую фразу?',
    sourcesDeleteConfirmHint: 'Клиенты с этой ссылкой больше не будут попадать в этот источник.',
    sourcesPhrase: 'Кодовая фраза',
    sourcesPhrasePlaceholder: 'Точный текст, который клиент отправит в WhatsApp',
    sourcesLanguage: 'Язык',
    sourcesEnabled: 'Включена',
    sourcesCopyLink: 'Скопировать ссылку',
    sourcesOpenLink: 'Открыть',
    sourcesLinkCopied: 'Ссылка скопирована',
    sourcesWaMeMissing: 'Задайте AGATA_WHATSAPP_PHONE, чтобы строить ссылки для клиентов',
    sourcesCreateTitle: 'Добавить источник',
    sourcesCreateHint: 'Связывает значение поля Источник с кодовой фразой WhatsApp и ссылкой wa.me.',
    sourcesEditTitle: 'Изменить источник',
    sourcesCreateFailed: 'Не удалось создать фразу',
    sourcesUpdateFailed: 'Не удалось обновить фразу',
    sourcesDeleteFailed: 'Не удалось удалить фразу',
    fields: 'Поля',
    responsible: 'Ответственный',
    pickFunnel: 'Выбрать воронку',
    addCard: 'Добавить карточку',
    createTitle: 'Новая карточка',
    createHint: 'Создаёт локальную карточку CRM в текущей воронке.',
    namePlaceholder: 'Имя лида',
    phonePlaceholder: 'Телефон',
    budgetPlaceholder: 'Бюджет (฿)',
    create: 'Создать',
    createFailed: 'Не удалось создать карточку',
    activity: 'Активность',
    activityEmpty: 'Активности пока нет',
    activityFromAmo: 'amoCRM',
    activityFromOperator: 'Оператор',
    activityCleared: 'очищено',
    activityCommentPlaceholder: 'Комментарий в активность…',
    activityCommentSend: 'Добавить',
    activityCommentFailed: 'Не удалось добавить комментарий',
    contacts: 'Контакты',
    contactsEmpty: 'Нет контактов',
    contactsGalleryEmpty: 'Пока нет контактов',
    contactsLoadFailed: 'Не удалось загрузить контакты',
    contactsSearch: 'Поиск контактов',
    contactCards: (count: number) =>
      count === 1 ? '1 сделка' : `${count} сделок`,
    contactMain: 'Основной',
    contactAdd: 'Добавить контакт',
    contactCreate: 'Создать контакт',
    contactCreateHint: 'Создаёт локальный контакт и привязывает его к карточке.',
    contactLink: 'Привязать имеющийся',
    contactLinkTitle: 'Привязать контакт',
    contactLinkHint: 'Привяжите существующий контакт к этой карточке.',
    contactLinkEmpty: 'Нет контактов для привязки',
    contactUnlink: 'Отвязать',
    contactUnlinkFailed: 'Не удалось отвязать контакт',
    contactNamePlaceholder: 'Имя контакта',
    contactPhonePlaceholder: 'Телефон',
    contactAddFailed: 'Не удалось добавить контакт',
    contactLinkFailed: 'Не удалось привязать контакт',
    responsibleNone: 'Не назначен',
    dialogNone: 'Нет диалога',
    contactTitle: 'Контакт',
    contactDescription: 'Карточка контакта CRM',
    contactNotFound: 'Контакт не найден',
    contactLoadFailed: 'Не удалось загрузить контакт',
    backToCard: 'Карточка',
    backToContacts: 'Контакты',
    handles: 'Каналы связи',
    handlesEmpty: 'Нет телефона, email или мессенджеров',
    channelAdd: 'Добавить канал',
    channelCreate: 'Создать канал',
    channelCreateHint: 'Добавляет телефон, email или мессенджер к контакту.',
    channelKind: 'Тип канала',
    channelValuePlaceholder: 'Телефон, email или chat id',
    channelAddFailed: 'Не удалось добавить канал',
    channelSaveFailed: 'Не удалось обновить канал',
    channelUnlink: 'Отвязать канал',
    channelUnlinkFailed: 'Не удалось отвязать канал',
    linkedCards: 'Сделки CRM',
    linkedCardsEmpty: 'Нет связанных сделок',
    names: {
      'Лиды в работе': 'Лиды в работе',
      'Отложенный спрос': 'Отложенный спрос',
      'Партнерская программа/Агенты': 'Партнерская программа/Агенты',
      Продавцы: 'Продавцы',
      'Пост продажный сервис': 'Пост продажный сервис',
      Архив: 'Архив',
      Неразобранное: 'Неразобранное',
      'новый лид': 'новый лид',
      'первичное касание': 'первичное касание',
      'не выходит на связь': 'не выходит на связь',
      'связались/подтвердили интерес': 'связались/подтвердили интерес',
      'Выявлены потребности': 'Выявлены потребности',
      'подбор объекта': 'подбор объекта',
      'Предложения отправлены': 'Предложения отправлены',
      'Переговоры/обсуждение': 'Переговоры/обсуждение',
      'назначен просмотр/встреча': 'назначен просмотр/встреча',
      'Подготовка сделки/депозит': 'Подготовка сделки/депозит',
      'На сделке': 'На сделке',
      'выплата комиссии': 'выплата комиссии',
      'некорректный контакт': 'некорректный контакт',
      'вышел на контакт': 'вышел на контакт',
      'квалификация проведена': 'квалификация проведена',
      Пассивный: 'Пассивный',
      активный: 'активный',
      база: 'база',
      интересуется: 'интересуется',
      'Отправили информацию': 'Отправили информацию',
      'Подключён/активирован': 'Подключён/активирован',
      'Привёл первого клиента': 'Привёл первого клиента',
      'Активный партнёр': 'Активный партнёр',
      Неактивный: 'Неактивный',
      'Новый продавец': 'Новый продавец',
      'ОЖИДАЕМ информацию': 'ОЖИДАЕМ информацию',
      'ПОдготовка объекта': 'ПОдготовка объекта',
      'ОТПРАВКА СММ/МЕНЕДЖЕРАМ': 'ОТПРАВКА СММ/МЕНЕДЖЕРАМ',
      'Размещен на сайте': 'Размещен на сайте',
      'актуализация объекта': 'актуализация объекта',
      'СРОЧНАЯ ПРОДАЖА': 'СРОЧНАЯ ПРОДАЖА',
      'Нет ответа': 'Нет ответа',
      РАЗОБРАТЬ: 'РАЗОБРАТЬ',
    },
  },
  systemKind: {
    crm_shadow: 'Тень CRM',
    crm: 'CRM',
    reminder: 'Напоминание',
    routing: 'Маршрутизация',
    info: 'Система',
  },
  playground: {
    title: 'Песочница',
    description: 'Переписка от лица посетителя маркетплейса через живой конвейер ответов',
    sessions: 'Сессии',
    recent: 'Недавние сессии песочницы',
    noSessions: 'Сессий пока нет.',
    noMessages: '(нет сообщений)',
    messageCount: (count: number) => `${count} сообщ.`,
    remarksCount: (open: number, total: number) => `${open}/${total} замеч.`,
    startFailed: 'Не удалось начать сессию',
    loadFailed: 'Не удалось загрузить диалог',
    noSession: 'Нет сессии',
    skipped: 'ИИ пропущен — ответ ещё ждёт подтверждения',
    sendFailed: 'Не удалось отправить',
    pushAdded: (number: number, offset: string) =>
      `Напоминание без ответа ${number}/3 добавлено как сообщение Agatha (+${offset})`,
    movedDeferred: 'Лид переведён в «Отложенный спрос»',
    noSeries: 'Нет активной серии напоминаний — Agatha должна сначала задать вопрос',
    simulate: (step: number) => `Симулировать шаг без ответа ${step}`,
    simulateButton: 'Симулировать молчание',
    simulationFailed: 'Симуляция не удалась',
    newChat: 'Новый чат',
    loadingDialog: 'Загрузка диалога',
    startHint: 'Отправьте сообщение, чтобы начать',
    thinking: 'Думает',
    placeholder: 'Спросите как покупатель…',
    send: 'Отправить',
    loadRunFailed: 'Не удалось загрузить прогон',
    hidePanel: 'Скрыть панель',
    showPanel: 'Показать панель',
    diagnostics: 'Диагностика',
  },
  knowledge: {
    title: 'Знания',
    description: 'Граф знаний и правки через чат с подтверждением.',
    snapshot: 'Снимок',
    backup: 'Скачать',
    restore: 'Восстановить',
    refresh: 'Обновить граф',
    graph: 'Граф',
    chat: 'Чат',
    details: 'Карточка',
    invalidFile: 'Некорректный JSON-файл',
    notObject: 'Снимок должен быть JSON-объектом',
    emptyEntities: 'В снимке должен быть непустой список сущностей',
    updated: 'Граф знаний обновлён',
    applied: (count: number) => `Применено изменений: ${count}`,
    chatFailed: 'Чат знаний не ответил',
    downloaded: (count: number) => `Скачано сущностей: ${count}`,
    backupFailed: 'Не удалось скачать снимок',
    restored: (count: number) => `Восстановлено сущностей: ${count}`,
    cleared: (count: number) => ` (очищено: ${count})`,
    restoreFailed: 'Не удалось восстановить снимок',
    confirmPrompt: 'Подтверди предложенные изменения.',
    rejectPrompt: 'Отклони предложенные изменения.',
    rejectedReply: 'Поняла — изменения не применялись.',
    loadFailed: 'Не удалось загрузить граф знаний',
    proposed: 'Предложенные изменения',
    placeholder: 'Спросите или попросите изменить знания…',
    intro:
      'Спросите про сущности, предложите правки и подтвердите план, прежде чем что-то будет записано.',
    selectNode: 'Выберите узел на графе, чтобы увидеть свойства и связи.',
    selectNodeShort: 'Выберите узел на графе.',
    noProperties: 'Нет свойств',
    outgoing: (count: number) => `Исходящие связи (${count})`,
    incoming: (count: number) => `Входящие связи (${count})`,
    entityDetails: 'Карточка сущности',
    entityHint: 'Свойства и связи сущности знаний',
  },
  remarks: {
    title: 'Замечания',
    forDialog: (id: string) => `Замечания по диалогу ${id}`,
    summary: (open: number, total: number) =>
      `Открыто ${open} из ${total}. Перетаскивайте карточки между колонками; локальный агент переносит их в «Исправлено».`,
    openDialog: 'Открыть диалог',
    clearFilter: 'Сбросить фильтр',
    loading: 'Загрузка замечаний…',
    loadFailed: 'Не удалось загрузить замечания',
    moveFailed: 'Не удалось переместить замечание',
    remarkId: 'Идентификатор',
    events: 'История',
    moveTo: 'Перенести в',
    commitRef: (ref: string) => `коммит ${ref}`,
    sheetHint: 'Карточка замечания и история',
    updatedAt: (when: string) => `Обновлено ${when}`,
    statuses: {
      new: 'Новое',
      in_progress: 'В работе',
      fixed: 'Исправлено',
      verified: 'Проверено',
      rejected: 'Отклонено',
    },
    categories: {
      reply_quality: 'Качество ответа',
      grounding: 'Опора на факты',
      journey: 'Путь клиента',
      tone: 'Тон',
      tooling: 'Инструменты',
      knowledge: 'Знания',
      other: 'Другое',
    },
    severity: {
      high: 'Высокая',
      medium: 'Средняя',
      low: 'Низкая',
    },
    eventCreated: 'Создано',
  },
  review: {
    proposed: (count: number) => `Предложенные замечания (${count})`,
    createOne: 'Создать замечание',
    createMany: (count: number) => `Создать замечания: ${count}`,
    created: (count: number) => `Создано замечаний: ${count}`,
    failed: 'Чат разбора не ответил',
    empty: 'Начните чат, чтобы оставить замечания по этому диалогу.',
    list: (count: number) => `Замечания (${count})`,
    board: 'Доска',
    loading: 'Загрузка переписки разбора…',
    intro:
      'Опишите, что в этом диалоге пошло не так. Разборщик сверяет отзыв с перепиской и диагностикой прогона, предлагает замечания и записывает их после вашего подтверждения.',
    reviewing: 'Разбирает…',
    placeholder: 'Что Agatha должна была сделать иначе?',
    send: 'Отправить',
  },
  diagnostics: {
    thinking: 'Ход агента',
    memory: 'Память',
    remarks: 'Замечания',
    skipped: (expr: string) => `пропущено: ${expr}`,
    notReached: 'не достигнут',
    tokens: (input: string, output: string) => `токены ${input} / ${output}`,
    query: (key: string) => `запрос ${key}`,
    contextAfter: 'Контекст после прогона',
    contextHint: 'Общая память и результаты шагов',
    tool: (name: string) => `Инструмент: ${name}`,
    skippedCondition: 'Условие пропуска',
    ifFalseStop: 'Если ложь: остановить конвейер',
    ifFalseContinue: 'Если ложь: продолжить',
    resolved: 'Вычисленные значения',
    contextIn: 'Контекст на входе',
    contextOut: 'Контекст / результат инструмента',
    received: 'Получено',
    guidance: 'Подсказка для составителя ответа',
    returned: 'Возвращено',
    generation: 'Инструкции генерации',
    requestMessages: 'Сообщения запроса',
    response: 'Ответ',
    finalContext: 'Итоговый контекст',
    emptySkill:
      'Навык не дал пригодного результата (пустой запрос, нет параметров или только подсказка).',
    emptyStep: 'Шаг не дал пригодного результата.',
    version: (id: string) => `версия ${id}`,
    startMemory: 'Начните чат, чтобы посмотреть долговременную память этого диалога.',
    loadingMemory: 'Загрузка памяти…',
    memoryFailed: 'Не удалось загрузить память',
    noMemory:
      'Память ещё не записана. После того как агент продвинет путь или запомнит факты, они появятся здесь.',
    interlocutorSummary: 'Сводка о собеседнике',
    dialogSummary: 'Сводка диалога',
    slots: (count: number) => `Слоты (${count})`,
    untitled: 'без названия',
    collapseGraph: 'Свернуть граф',
    expandGraph: 'Развернуть граф',
    promptChars: (count: string) => `${count} симв.`,
    prompt: 'промпт',
    skills: 'навыки:',
    loadingRun: 'Загрузка прогона…',
    noSteps: 'Для этого прогона нет диагностики шагов.',
    stepsHint:
      'Вызовы инструментов появляются здесь схемой после каждого ответа. Нажмите блок, чтобы раскрыть подробности.',
    notReachedReason: (reason: string) => `Не достигнут: ${reason}`,
    llm: {
      skill_route: 'Выбор навыков',
      compose_reply: 'Составление ответа',
      extract_slots: 'Извлечение памяти',
    },
    status: {
      succeeded: 'Успех',
      failed: 'Ошибка',
      skipped: 'Пропущен',
      running: 'Выполняется',
      empty: 'Пусто',
      not_reached: 'Не достигнут',
    },
    kind: {
      step: 'Шаг',
      skill: 'Навык',
      skill_step: 'Шаг навыка',
      llm: 'LLM',
      context: 'Контекст',
    },
  },
  fields: {
    budget: 'Бюджет',
    district: 'Район',
    property_type: 'Тип объекта',
    handoff_reason: 'Причина передачи',
    owner_location: 'Локация собственника',
    language: 'Язык',
    bedrooms: 'Спальни',
    role: 'Роль',
    partner: 'Партнер',
    partner_commission: 'Комиссия партнера',
    advertising: 'Реклама',
    call_recording: 'Аудиозапись звонка',
    lead_bucket: 'Температура лида',
    lead_source: 'Источник',
    hot: 'Горячий',
    warm: 'Тёплый',
    junk: 'Мусор',
    quiz: 'Квиз',
    alliance: 'Альянс',
    marketplace: 'Маркетплейс',
    dialog_summary: 'Описание',
    ad_id: 'Ad ID',
    ad_set_id: 'Ad set ID',
    campaign_name: 'Campaign name',
    utm_source: 'utm_source',
    utm_medium: 'utm_medium',
    utm_campaign: 'utm_campaign',
    utm_content: 'utm_content',
    utm_term: 'utm_term',
    utm_referrer: 'utm_referrer',
    email: 'Email',
    channel: 'Канал',
    phone: 'Телефон',
    whatsapplid_wz: 'WhatsApp LID',
    whatsapplid_waapi: 'WhatsApp LID (WaAPI)',
    tgid: 'Telegram ID',
    tgusername: 'Telegram username',
    telegram: 'Telegram',
    должность: 'Должность',
    город: 'Город',
    сфера: 'Сфера',
    опыт: 'Опыт',
    комиссия_партнера: 'Комиссия партнера',
  },
};
